import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API_BASE = `${ENV_API_BASE_URL}/api`;
const STATUS_OPTIONS = ["pending", "confirmed", "cancelled"];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  address: "",
  country: "",
  source: "",
  courseId: "",
  batchId: "",
  roomType: "",
  seats: 1,
  status: "pending",
};

const formatDateDisplay = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
};

const formatBatchLabel = (batch) => {
  if (!batch || typeof batch === "string") return "Unknown Batch";
  return `${formatDateDisplay(batch.startDate)} - ${formatDateDisplay(batch.endDate)}`;
};

const roomTypeLabel = (type) => {
  switch (type) {
    case "shared":
      return "Shared Room";
    case "private":
      return "Private Room";
    case "couple":
      return "Couple Room";
    case "duplex":
      return "Duplex Room";
    default:
      return type || "General";
  }
};

function CourseBookings() {
  const [bookings, setBookings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchBookings(), fetchCourses(), fetchBatches()]);
    };

    load();
  }, []);

  const fetchBookings = async () => {
    const res = await axios.get(`${API_BASE}/course-bookings`, {
      params: { populate: true },
    });
    setBookings(res.data?.data || []);
  };

  const fetchCourses = async () => {
    const res = await axios.get(`${API_BASE}/courses`);
    setCourses(res.data?.data || []);
  };

  const fetchBatches = async () => {
    const res = await axios.get(`${API_BASE}/course-batches`);
    setBatches(res.data?.data || []);
  };

  const filteredBatches = useMemo(
    () =>
      batches.filter((batch) => {
        const courseId = typeof batch.course === "object" ? batch.course._id : batch.course;
        return courseId === formData.courseId;
      }),
    [batches, formData.courseId]
  );

  const selectedBatch = useMemo(
    () => filteredBatches.find((batch) => batch._id === formData.batchId),
    [filteredBatches, formData.batchId]
  );

  const availableRoomOptions = selectedBatch?.roomOptions || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "courseId") {
        next.batchId = "";
        next.roomType = "";
      }

      if (name === "batchId") {
        next.roomType = "";
      }

      return next;
    });
  };

  const handleEdit = (item) => {
    const courseId = typeof item.course === "object" ? item.course._id : item.course;
    const batchId = typeof item.batch === "object" ? item.batch._id : item.batch;

    setFormData({
      name: item.name || "",
      email: item.email || "",
      phone: item.phone || "",
      address: item.address || "",
      country: item.country || "",
      source: item.source || "",
      courseId: courseId || "",
      batchId: batchId || "",
      roomType: item.roomType || "",
      seats: item.seats || 1,
      status: item.status || "pending",
    });
    setEditId(item._id);
    setShowForm(true);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        country: formData.country,
        source: formData.source,
        course: formData.courseId,
        batch: formData.batchId,
        roomType: formData.roomType,
        seats: Number(formData.seats || 1),
        status: formData.status,
      };

      await axios.put(`${API_BASE}/course-bookings/${editId}`, payload);
      await fetchBookings();
      await fetchBatches();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update booking.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API_BASE}/course-bookings/${id}`);
      await fetchBookings();
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete booking.");
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditId(null);
    setShowForm(false);
  };

  return (
    <div className="container pt-5 mt-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1">Course Bookings</h3>
          <p className="text-muted mb-0">
            Confirm, cancel, or update bookings. Confirmed bookings reduce seats,
            cancelled bookings release them again.
          </p>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {!showForm ? (
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Email</th>
                <th>Course</th>
                <th>Batch</th>
                <th>Type</th>
                <th>Price</th>
                <th>Seats</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {bookings.map((item, index) => (
                <tr key={item._id}>
                  <td>{index + 1}</td>
                  <td>{item.name}</td>
                  <td>{item.email}</td>
                  <td>
                    {typeof item.course === "object"
                      ? item.course.title || item.course.shortTitle
                      : "Unknown Course"}
                  </td>
                  <td>{formatBatchLabel(item.batch)}</td>
                  <td>{roomTypeLabel(item.roomType)}</td>
                  <td>{item.price || "-"}</td>
                  <td>{item.seats}</td>
                  <td>
                    <span
                      className={`badge ${
                        item.status === "confirmed"
                          ? "bg-success"
                          : item.status === "cancelled"
                          ? "bg-danger"
                          : "bg-warning text-dark"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td>{formatDateDisplay(item.createdAt)}</td>
                  <td>
                    <button
                      className="btn btn-warning btn-sm me-2"
                      onClick={() => handleEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="card p-4 shadow-sm">
          <div className="d-flex justify-content-between mb-3">
            <h5>Edit Booking</h5>
            <button type="button" className="btn-close" onClick={resetForm} />
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Course</label>
              <select
                name="courseId"
                className="form-select"
                value={formData.courseId}
                onChange={handleChange}
                required
              >
                <option value="">Select Course</option>
                {courses.map((course) => (
                  <option key={course._id} value={course._id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Batch</label>
              <select
                name="batchId"
                className="form-select"
                value={formData.batchId}
                onChange={handleChange}
                required
              >
                <option value="">Select Batch</option>
                {filteredBatches.map((batch) => (
                  <option key={batch._id} value={batch._id}>
                    {formatBatchLabel(batch)} ({batch.availableSeats} seats left)
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Room Type</label>
              <select
                name="roomType"
                className="form-select"
                value={formData.roomType}
                onChange={handleChange}
                required
              >
                <option value="">Select Type</option>
                {availableRoomOptions.map((option) => (
                  <option key={option.type} value={option.type}>
                    {option.label} ({option.availableSeats}/{option.capacity} left)
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Seats</label>
              <input
                type="number"
                name="seats"
                className="form-control"
                min="1"
                value={formData.seats}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Status</label>
              <select
                name="status"
                className="form-select"
                value={formData.status}
                onChange={handleChange}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Name</label>
              <input
                type="text"
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Email</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Phone</label>
              <input
                type="text"
                name="phone"
                className="form-control"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Country</label>
              <input
                type="text"
                name="country"
                className="form-control"
                value={formData.country}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Source</label>
              <input
                type="text"
                name="source"
                className="form-control"
                value={formData.source}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Address</label>
              <input
                type="text"
                name="address"
                className="form-control"
                value={formData.address}
                onChange={handleChange}
              />
            </div>
          </div>

          {selectedBatch && formData.roomType && (
            <div className="alert alert-info">
              Selected batch: {formatBatchLabel(selectedBatch)}. Selected type:{" "}
              {roomTypeLabel(formData.roomType)}.
            </div>
          )}

          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-success">Update</button>
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default CourseBookings;
